import { atom } from 'jotai'
import type { DripSettings } from '@/brush/drip'

import { Position } from './voxelMath'

import type { AxisDragState } from './overlays/axis'
import type { ReactElement } from 'react'
import type { ToolType } from '@/events/types'
import type { Face, Hit } from './voxelTypes'
import type { Spine } from '@/interactions/spineCore'
import type { ColorPalette } from './constants/scene'
import type { ShapeBuilderParams } from '@/builders/shapeBuilder'
import type { Axis, DrawingSurfaces } from '@/interactions/drawingSurfaces'
import type { BrushNibSettings } from '@/brush/nib'
import type { HoverPreviewMode } from '@/voxels/hoverPreviewMode'

// ============================================================================
// Types
// ============================================================================

export interface ScribbleState {
  positions: Set<number>  // Position keys for accumulated scribble
  surfaceNormals: Map<number, { x: number; y: number; z: number }>  // For proper orientation
  lastPos: Position | null
  faceOp?: 'add' | 'remove' | null
  face?: Face
}

export interface CameraState {
  position: Position
  target: Position
}

// Tool state interface
export interface PaintSettings {
  length?: number
  depth?: number
  softness?: number     // 0..1
  opacity?: number      // 0..1
  blend?: 'normal' | 'multiply' | 'screen' | 'burn'
  shape?: 'sphere' | 'line'
  lineAxis?: 'x' | 'y' | 'z' | 'auto'
  // Paint toggles
  applyColor?: boolean
  applyType?: boolean
}

export interface EraserSettings {
  mode?: 'erase' | 'fill'
  /** Idle hover preview (`src/voxels/hoverPreviewMode.ts`); unset = `auto`. */
  hover?: HoverPreviewMode
  /**
   * What the eraser removes (materials-perf-plan B5, Q11): 'self' = only the material under the
   * first hit (same canonical type), 'any' = everything the brush covers. Unset = the registry
   * `digScope` of the type under the first hit (bulk materials 'self', everything else 'any').
   */
  scope?: 'self' | 'any'
  /**
   * Protected canonical types (default none): the eraser never removes them, and its preview never
   * shows them erased. Stored in each stroke's snapshot (`BrushSnapshot.keep`), so replay is exact.
   * Setting it sends eraser strokes down the swept path (sphere, centre placement).
   */
  protect?: string[]
  /**
   * Keep digging over one's own path (default off): within one stroke, ground dug by samples the
   * pointer has moved away from becomes pickable again, so dragging back over a trench digs it
   * deeper, while a pointer resting in place digs nothing more (`src/interactions/redig.ts`). Swept
   * path only, like `protect`.
   */
  redig?: boolean
  /**
   * Press and hold in place switches to the rectangle eraser, reverting the dig so far (default
   * true, the editor's gesture; page tools write false unless asked). Protected erasers never switch.
   */
  holdForRectangle?: boolean
}

export interface ScribbleSettings {
  profile?: 'pencil'
  /** Idle hover preview (`src/voxels/hoverPreviewMode.ts`); unset = `auto`. */
  hover?: HoverPreviewMode
  radius?: number
  noRepeatCount?: number
  // When true, connect successive spine centers with 6-connected steps
  shouldConnect?: boolean
  // Minimum local connection range (in voxels), combined with 2*radius
  connectMinRange?: number
  // Override diagonal-bridging policy for connection (otherwise inferred by voxel type)
  bridgeDiagonals?: boolean
  // Multi-voxel shape for scribble expansion
  shape?: 'sphere' | 'cube' | 'disc'
  // Placement for multi-voxel shapes relative to hit position
  placement?: 'center' | 'onTop'
  // Spacing between successive centers (in voxels) when connected
  spacingVoxels?: number
  // Swept-brush-only options (default off; setting any of them sends the stroke down the swept path):
  // speed-dependent width (src/brush/nib.ts), with `radius` as the slow, full width
  nib?: BrushNibSettings
  // sample centres follow the exact touch point within the face (1/16 voxel) instead of cell centres
  subcell?: boolean
  // drawing writes empty cells only ('fill' instead of 'place'): the stroke sits on what is there
  // instead of replacing it (e.g. blob ink on solid paper)
  fillOnly?: boolean
  // pour to a level (verb 'level'): the stroke fills each column under the brush up to one height,
  // `level` cells above the surface where the stroke starts, so overlapping strokes stay flat
  // (enamel fields, plates, backgrounds) instead of terracing. Swept-only, like the nib.
  level?: number
  // bead shape for swept draw strokes (default a centred sphere): `lens` sinks a big sphere so only a
  // low dome `thickness` cells high shows, the stroke's width at the surface (paint). Composes with
  // the nib and grow on hold (each sample's dome follows its own width).
  bead?: { shape: 'sphere' | 'lens'; thickness?: number }
  // canonical voxel types a draw stroke never overwrites ('place' and 'coat'; e.g. a canvas ground
  // under paint, so a sunk lens bead or a big sphere never eats into it). Swept-only.
  protect?: string[]
  // coat (verb 'coat'): a shell `thickness` cells thick on the surface as it was before the stroke,
  // recolouring the exposed surface under the brush; thickness 0 recolours only (paint, no relief)
  coat?: { thickness: number }
  // a press held still grows the brush (a tap becomes a blob that swells while the finger stays
  // down), up to `maxRadius`, `perSecond` voxels a second; moving on keeps the grown size
  growOnHold?: { maxRadius: number; perSecond: number }
  // drips (src/brush/drip.ts, src/interactions/brushDrips.ts): drips hang from the stroke and run
  // down the surface while the finger is down; they end in a bulb where they stand when it lifts
  drip?: DripSettings
  // picking ignores the mirror planes, so strokes near a mirror land on the surface behind it
  skipMirrorPlanes?: boolean
  // press and hold in place switches to the rectangle tool, reverting the stroke so far (default
  // true, the editor's gesture; page tools write false unless asked). Applies to the draw tool only;
  // the eraser has its own `EraserSettings.holdForRectangle`.
  holdForRectangle?: boolean
}

export interface RectangleSettings {
  fillMode: boolean
}

export interface ToolState {
  currentTool: ToolType
  paletteIndex: number
  selectedShape: 'sphere' | 'cylinder' | 'cone' | null  // Which shape is selected when shape tool is active
  // Remember chosen swatch theme for color picker
  // Nested, typed settings
  paint: PaintSettings
  eraser: EraserSettings
  scribble: ScribbleSettings
  rectangle: RectangleSettings
}

// Overlay interface
export interface Overlay {
  render: () => ReactElement
  meta?: {
    feature?: string
    priority?: number
  }
}

export interface ShapeState {
  type: 'sphere' | 'cylinder' | 'cone'
  center: Position | null
  radius: number
  height?: number  // For cylinder and cone
  axis?: 'x' | 'y' | 'z'  // Orientation axis
  axisSign?: 1 | -1
  startPosition?: Position  // For corner-based resizing
  sizingStage?: 'radius' | 'height' | null
  // Beam visualization cache for UI overlays (avoid recalculation in controller)
  beamOrigin?: Position
  beamDir?: { x: number; y: number; z: number }
}

export interface VoxelSelectionState {
  keys: number[]           // Position keys for selected voxels
  center: Position           // The center point for axis manipulation
  translate: Position | null       // Current translation delta {x, y, z}
  boundingBox: {                  // Bounding box of the original selection
    min: Position
    max: Position
  }
  isDragging?: boolean       // True when free-dragging the selection (not using axis/plane)
}

export type SelectionAxisDragRequest = {
  pointerId: number
  origin?: { x: number; y: number }
  axis?: 'x' | 'y' | 'z'
  center?: Position
  // Optional target for axis drag: defaults to selection when omitted
  target?: 'selection' | 'subject'
}

export type RotationDragRequest = {
  pointerId: number
  axis: 'x' | 'y' | 'z'
  // World-space rotation pivot (subject center)
  anchor: Position
  // World-space center of the visible gizmo ring (for screen-space angle mapping)
  gizmoCenter?: Position
}

export type PlaneDragRequest =
  | { target: { kind: 'mirror'; axis: Axis }; axis: Axis; origin: Position }
  | { target: { kind: 'drawingSurface'; id: string }; axis: Axis; origin: Position }

export interface FaceSelectionState {
  // Selected face direction (one direction only)
  face: Face
  // Either a contiguous plate defined by keys, or an anchor position for contiguous-face detection
  keys?: Set<number>
  anchor?: Position
  // Live extrusion offset for UI (0 when previewing plate, null when inactive)
  offset?: number | null
}

export interface EphemeralState {
  // UI interaction state
  isDrawing: boolean
  initialized: boolean
  drawingStartPos: Position | null
  cursorScreenPos: { x: number; y: number } | null
  cursorWorldPos: { x: number; y: number; z: number } | null  // 3D position where cursor hits scene
  cursorNormal: { x: number; y: number; z: number } | null
  cursorHit: Hit | null
  // Reference-counted suppression; hoverSuppressed is derived from count > 0
  shiftDown: boolean  // Track shift for snapping behaviors
  // Constraint toggles during shape sizing (for touch devices)
  shapeConstrained: boolean
  // Sticky selection op for tablet: replace | add | subtract
  selectOp: 'replace' | 'add' | 'subtract'

  // Tool state
  tool: ToolState
  // Document bounds (persisted via durable UI)
  bounds?: { x: number; y: number; z: number }
  // Selection rectangle UI flags used by controller
  isSelecting?: boolean
  selectionStartPos?: Position | null
  // Eyedropper hover position for visual feedback
  eyedropperHoverPos?: Position | null
  // Transient pointer helpers for line/spline math
  clientPos?: { clientX: number; clientY: number } | null
  canvasRect?: { left: number; top: number; width: number; height: number } | null

  // Preview models (computed by gesture system)
  scribbleModel: ScribbleState | null
  rectangleModel: {
    type: 'rectangle'
    boundingBox: { min: Position; max: Position }
    positions: Set<number>
    mode: 'add' | 'erase'
  } | null

  // Rectangle visualization bounds
  rectangleBounds: { min: Position; max: Position; color: number } | null

  // Rectangle initial normal for proper plane orientation
  rectangleInitialNormal: { x: number; y: number; z: number } | null

  // Shape mode state
  shapeState: ShapeState | null
  shapeDragStart: { x: number; y: number; initialCenter?: Position; activeAxis?: 'x' | 'y' | 'z'; initialAxisPos?: number } | null

  // Selection state
  voxelSelection: VoxelSelectionState | null
  // Face selection for extrusion
  faceSelection: FaceSelectionState | null
  axisDragState: AxisDragState | null
  axisHovered: string | null  // 'x', 'y', 'z', 'xy', 'xz', 'yz'

  // Free-drag plane info for rendering a guide
  dragPlane?: { normal: { x: number; y: number; z: number }, origin: { x: number; y: number; z: number } } | null
  drawingSurfaces: DrawingSurfaces

  // In-progress spines for derived previews/commit
  // Paint spine: sequence of centers sampled along the stroke
  paintSpine?: Map<number, { normal?: { x: number; y: number; z: number } }> | null
  // Scribble spine: ordered per-center voxel + seed normal
  scribbleSpine?: Spine | null
  colorPalette: ColorPalette

  mirrorCreateHandleVisible: boolean
  planeDragRequest: PlaneDragRequest | null
  selectionAxisDragRequest: SelectionAxisDragRequest | null
  rotationDragRequest: RotationDragRequest | null
  builderSubject: { id: string; params: ShapeBuilderParams } | null
  activeSubjectRef: { kind: string; id: string } | null
  subjectTransforms: Record<string, { translate: Position; rotation: { x: number; y: number; z: number; w: number } }>
  // Rotation gizmo active (dragging) — used to hide move gizmo during rotation
  rotationActive?: boolean

}

// ============================================================================
// Initial State & Helpers
// ============================================================================


export const initialEphemeralState: EphemeralState = {
  colorPalette: 'PICO-8',
  isDrawing: false,
  drawingStartPos: null,
  cursorScreenPos: null,
  cursorWorldPos: null,
  cursorNormal: null,
  cursorHit: null,
  shiftDown: false,
  shapeConstrained: false,
  selectOp: 'replace',
  initialized: false,
  isSelecting: false,
  selectionStartPos: null,
  clientPos: null,
  canvasRect: null,

  tool: {
    // Sync default block type with first palette item (e.g., 'googly_eye')
    currentTool: 'scribble' as ToolType,
    paletteIndex: 0,
    selectedShape: null,
    paint: {
      depth: 1,
      softness: 0,
      opacity: 1,
      blend: 'normal',
      shape: 'sphere',
      lineAxis: 'auto',
      applyColor: true,
      applyType: true,
    },
    eraser: {
      mode: 'erase',
    },
    scribble: {
      profile: 'pencil',
      radius: 0.5,
      noRepeatCount: 12,
      shouldConnect: true,
      shape: 'sphere',
      placement: 'center',
      spacingVoxels: 1,
    },
    rectangle: {
      fillMode: false,
    },
  },

  scribbleModel: null,
  rectangleModel: null,

  rectangleBounds: null,
  rectangleInitialNormal: null,

  shapeState: null,
  shapeDragStart: null,

  voxelSelection: null,
  faceSelection: null,
  axisDragState: null,
  axisHovered: null,
  dragPlane: null,
  drawingSurfaces: {},
  paintSpine: null,
  scribbleSpine: null,
  mirrorCreateHandleVisible: false,
  planeDragRequest: null,
  selectionAxisDragRequest: null,
  rotationDragRequest: null,
  builderSubject: null,
  activeSubjectRef: null,
  subjectTransforms: {},
  rotationActive: false,

}

// ============================================================================
// Root Atom
// ============================================================================

export const uiAtom = atom<EphemeralState>(initialEphemeralState)
const uiProp = <K extends keyof EphemeralState>(k: K) =>
  atom((get) => get(uiAtom)[k] as EphemeralState[K])

export const cursorScreenPosAtom = uiProp('cursorScreenPos')
export const cursorWorldPosAtom = uiProp('cursorWorldPos')
export const faceSelectionAtom = uiProp('faceSelection')
export const rectangleBoundsAtom = uiProp('rectangleBounds')
export const eyedropperHoverPosAtom = uiProp('eyedropperHoverPos')
export const cursorHitAtom = uiProp('cursorHit')
export const axisDragStateAtom = uiProp('axisDragState')
export const axisHoveredAtom = uiProp('axisHovered')
export const drawingSurfacesAtom = uiProp('drawingSurfaces')
export const activeSubjectRefAtom = uiProp('activeSubjectRef')
export const savedPaletteAtom = uiProp('colorPalette')
export const mirrorCreateHandleVisibleAtom = uiProp('mirrorCreateHandleVisible')
export const shapeConstrainedAtom = uiProp('shapeConstrained')
export const rotationActiveAtom = atom((get) => !!get(uiAtom).rotationActive)

export const toolAtom = uiProp('tool')
export const initializedAtom = uiProp('initialized')
export const paletteIndexAtom = atom((get) => get(toolAtom).paletteIndex)

export const voxelSelectionAtomRaw = uiProp('voxelSelection')
export const shapeStateAtom = uiProp('shapeState')
export const shiftDownAtom = uiProp('shiftDown')
export const selectOpAtom = uiProp('selectOp')

// Derived: whether selection contour/overlays should consider dragging active
export const isSelectionDraggingAtom = atom((get) => {
  const sel = get(voxelSelectionAtomRaw)
  return !!(sel?.isDragging || get(axisDragStateAtom))
})
